import api from '@/services/utils/apiInstance.ts'
import type { AxiosResponse } from 'axios';
import type {
  LoginRequest,
  OTPRequest,
  LogoutResponse,
  MeResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  CreatePasswordRequest
} from '@/types/services/auth/auth.type.ts';

/** Raised when /auth/me returns an account that has no back-office access. */
export class NoBackOfficeAccessError extends Error {
  constructor() {
    super('User has no back-office access')
    this.name = 'NoBackOfficeAccessError'
  }
}

class AuthService {
  async login(userData: LoginRequest): Promise<void> {
    await api.post('/auth/login/bo', {
      email: userData.email,
      password: userData.password
    });
  }

  async loginOTP(userData: OTPRequest): Promise<void> {
    await api.post('/auth/login/bo/otp', {
      email: userData.email,
      otpSecret: userData.otpSecret,
    });
  }

  async me(): Promise<MeResponse> {
    const response: AxiosResponse = await api.get('/auth/me')

    // Defense in depth: the API only opens BO sessions for BO accounts, but never
    // keep a session in the back office for an account without BO access.
    if (response.data.is_access_bo !== true) {
      throw new NoBackOfficeAccessError()
    }

    const user: MeResponse = {
      id: response.data.id,
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      role: response.data.role,
      permissions: response.data.permissions,
      isAccessBo: response.data.is_access_bo,
    }
    localStorage.setItem('user', JSON.stringify(user))

    return user
  }

  async logout(): Promise<LogoutResponse> {
    const response: AxiosResponse = await api.get('/auth/logout');

    return {
      statusCode: response.status
    }
  }

  async forgotPassword(userData: ForgotPasswordRequest): Promise<void> {
    await api.post('/auth/forgot-password/bo', {
      email: userData.email,
    });
  }

  async resetPassword(userData: ResetPasswordRequest): Promise<void> {
    await api.patch('/auth/reset-password', {
      password: userData.password,
      confirmPassword: userData.confirmPassword,
      resetToken: userData.resetToken
    });
  }

  async createPassword(userData: CreatePasswordRequest): Promise<void> {
    await api.post('/auth/create-password', {
      password: userData.password,
      confirmPassword: userData.confirmPassword,
      creationAccountToken: userData.creationAccountToken,
    });
  }
}

export default new AuthService();
